<template>
  <div class="auth-form">
    <!--  header --->
    <div class="auth-head">
      <h2 class="auth-title">{{ headerTitle }}</h2>
      <p v-if="headerSubtitle" class="auth-subtitle" v-html="headerSubtitle"></p>
      <p v-else-if="isLoginStep || showOtpForm" class="auth-subtitle">{{ $t('auth login subtitle') }}</p>
    </div>

    <!--  password / otp switch --->
    <div v-if="isLoginStep || showOtpForm" class="auth-segment">
      <button type="button" :class="{ active: isLoginStep }" @click="returnToLogin">
        {{ $t("password") }}
      </button>
      <button type="button" :class="{ active: showOtpForm }" @click="openOTP">
        {{ $t("login with otp") }}
      </button>
    </div>

    <!--  login / otp / forget password steps (single panel to avoid layout jump) --->
    <v-form ref="login$forgetPassword" class="ma-0 pa-0 auth-step-wrap" @submit.prevent>
      <transition name="step">
        <!--  login --->
        <div v-if="isLoginStep" key="login" class="auth-fields">
          <v-text-field
            v-model="phone"
            outlined
            hide-details="auto"
            :label="$t('mobile')"
            placeholder="0912-12xxxx7"
            class="rtl-text auth-input"
            prepend-inner-icon="mdi-cellphone"
            type="tel"
            inputmode="numeric"
            autocomplete="username"
            clearable
            :rules="[mobileFormatRule]"
          ></v-text-field>
          <v-text-field
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
            @click:append="showPassword = !showPassword"
            outlined
            hide-details="auto"
            :label="$t('password')"
            class="rtl-text auth-input"
            prepend-inner-icon="mdi-lock-outline"
            autocomplete="current-password"
            @keydown.enter.prevent="login"
          ></v-text-field>
          <div class="auth-row">
            <span>{{ $t("forgetPassword") }}</span>
            <button type="button" class="auth-link" @click="stepForgetPassword">
              {{ $t("reset password") }}
            </button>
          </div>
          <div class="auth-actions">
            <v-btn :loading="loading" block depressed class="auth-btn auth-btn--primary" @click="login">
              {{ $t("Login") }}
            </v-btn>
          </div>
        </div>

        <!-- send OTP code --->
        <div v-else-if="showOtpForm" key="otp-send" class="auth-fields">
          <v-text-field
            v-model="phone"
            outlined
            :label="$t('mobile')"
            clearable
            class="rtl-text auth-input"
            prepend-inner-icon="mdi-cellphone"
            type="tel"
            inputmode="numeric"
            placeholder="0912-12xxxx7"
            hide-details="auto"
            :rules="[mobileFormatRule]"
            @keydown.enter.prevent="getOTPCode"
          ></v-text-field>
          <div class="auth-actions">
            <v-btn :loading="loading" block depressed class="auth-btn auth-btn--primary" @click="getOTPCode">
              {{ $t("send code") }}
            </v-btn>
          </div>
        </div>

        <!-- verify OTP code & login --->
        <div v-else-if="OTPVerifyStep" key="otp-verify" class="auth-fields">
          <v-text-field
            v-model="OTPcode"
            outlined
            :label="$t('send otp')"
            class="rtl-text auth-input otp-input"
            prepend-inner-icon="mdi-message-lock-outline"
            inputmode="numeric"
            autocomplete="one-time-code"
            hide-details="auto"
            clearable
            @keydown.enter.prevent="confirmOTP()"
          ></v-text-field>
          <div class="auth-row">
            <span>{{ $t("resend code again") }}</span>
            <button type="button" class="auth-link auth-timer" :disabled="timer > 0" @click="resendOTP">
              <v-icon x-small :color="timer > 0 ? 'grey' : 'primary'">mdi-timer-outline</v-icon>
              {{ otpButtonText }}
            </button>
          </div>
          <div class="auth-actions">
            <v-btn :loading="loading" block depressed class="auth-btn auth-btn--primary" @click="confirmOTP()">
              {{ $t("Login") }}
            </v-btn>
            <div class="auth-row">
              <span>{{ $t("wrong number") }}</span>
              <button type="button" class="auth-link" @click="EditPhone">{{ $t("edit phone number") }}</button>
            </div>
          </div>
        </div>

        <!--  OTP forgetPassword step -->
        <div v-else-if="OTPResetPassword" key="otp-reset" class="auth-fields">
          <v-text-field
            v-model="phone"
            outlined
            :label="$t('mobile')"
            clearable
            class="rtl-text auth-input"
            prepend-inner-icon="mdi-cellphone"
            type="tel"
            inputmode="numeric"
            placeholder="0912-12xxxx7"
            hide-details="auto"
            :rules="[mobileFormatRule]"
            @keydown.enter.prevent="getOTPCodePassword"
          ></v-text-field>
          <div class="auth-actions">
            <v-btn :loading="loading" block depressed class="auth-btn auth-btn--primary" @click="getOTPCodePassword">
              {{ $t("send code") }}
            </v-btn>
            <button type="button" class="auth-back" @click="OTPResetPassword = false">
              <v-icon small>{{ $vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
              {{ $t("log in") }}
            </button>
          </div>
        </div>

        <!--  forgetPassword step -->
        <div v-else-if="showResetPassword" key="reset" class="auth-fields">
          <v-text-field
            v-model="OTPcode"
            outlined
            :label="$t('send otp')"
            class="rtl-text auth-input otp-input"
            prepend-inner-icon="mdi-message-lock-outline"
            maxlength="5"
            inputmode="numeric"
            autocomplete="one-time-code"
            hide-details="auto"
            clearable
          ></v-text-field>

          <div class="auth-row">
            <span>{{ $t("resend code again") }}</span>
            <button type="button" class="auth-link auth-timer" :disabled="timer > 0" @click="resendOTP">
              <v-icon x-small :color="timer > 0 ? 'grey' : 'primary'">mdi-timer-outline</v-icon>
              {{ otpButtonText }}
            </button>
          </div>

          <p class="auth-subtitle" v-html="$t('choose secure password')"></p>
          <v-text-field
            v-model="newPassword"
            outlined
            :label="$t('password')"
            class="rtl-text auth-input"
            prepend-inner-icon="mdi-lock-outline"
            hide-details="auto"
            autocomplete="new-password"
            clearable
          ></v-text-field>
          <v-text-field
            v-model="retypePassword"
            outlined
            :label="$t('repeat password')"
            class="rtl-text auth-input"
            prepend-inner-icon="mdi-lock-check-outline"
            hide-details="auto"
            clearable
            :rules="[confirmPasswordRule]"
            @keydown.enter.prevent="resetPassword"
          ></v-text-field>

          <!-- pass rules section -->
          <div class="rules-grid">
            <div
              v-for="r in passwordRuleList"
              :key="r.key"
              class="rule-line"
              :class="ruleClass(passwordChecks[r.key])"
            >
              <v-icon x-small :color="iconColor(passwordChecks[r.key])">{{ iconName(passwordChecks[r.key]) }}</v-icon>
              <span>{{ $t(r.label) }}</span>
            </div>
          </div>

          <div class="auth-actions">
            <v-btn :loading="loading" block depressed class="auth-btn auth-btn--primary" @click="resetPassword">
              {{ $t("confirm new password") }}
            </v-btn>
            <div class="auth-row">
              <span>{{ $t("wrong number") }}</span>
              <button type="button" class="auth-link" @click="EditPhonePass">{{ $t("edit phone number") }}</button>
            </div>
          </div>
        </div>
      </transition>
    </v-form>

    <!-- divider section to singUp button--->
    <template v-if="isLoginStep || showOtpForm">
      <div class="auth-divider">{{ $t("or") }}</div>
      <v-btn block depressed class="auth-btn auth-btn--ghost" @click="routeToRegister">
        <v-icon left small>mdi-account-plus-outline</v-icon>
        {{ buttonText }}
      </v-btn>
    </template>

    <!--  footer --->
    <p v-if="!showResetPassword" class="auth-terms">
      {{ $t("login terms&condition") }}
      <a href="#">{{ $t("terms of use") }}</a>
      {{ $t("terms of service and policy") }}
      {{ $t("abriron is") }}
    </p>

    <!--  snackbar --->
    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          :loading="loading"
          color="white"
          text
          v-bind="attrs"
          @click="snakbar = !snakbar"
        >
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import General from '@/Mixins/general'

export default {
  name: 'LoginForm',
  components: {},
  mixins: [General],
  props: {
    SingUpSection: Boolean
  },
  data: () => ({
    loading: false,
    phone: '',
    password: '',
    newPassword: '',
    retypePassword: '',
    OTPcode: '',
    showSingUp: false,
    showOtpForm: false,
    showPassword: false,
    OTPVerifyStep: false,
    OTPResetPassword: false,
    showResetPassword: false,
    mobileFormatRule: null,
    timer: 120,
    snakbar: false,
    snakbarColor: null,
    snakbarText: null,
    userData: []
  }),
  created () {
    this.mobileFormatRule = (value) => {
      const regex = /^(\+98|0)?9\d{9}$/
      return regex.test(value) ? true : this.$t('invalid mobile format')
    }
  },
  computed: {
    isLoginStep () {
      return !this.showOtpForm && !this.showResetPassword && !this.showSingUp && !this.OTPVerifyStep && !this.OTPResetPassword
    },
    passwordRuleList () {
      return [
        { key: 'length', label: 'min length 8' },
        { key: 'upperLower', label: 'must include uppercase and lowercase' },
        { key: 'number', label: 'must include number' },
        { key: 'symbol', label: 'must include symbol' }
      ]
    },
    passwordChecks () {
      const pass = this.newPassword || ''
      return {
        length: pass.length >= 8,
        upperLower: /[A-Z]/.test(pass) && /[a-z]/.test(pass),
        number: /\d/.test(pass),
        symbol: /[^A-Za-z0-9]/.test(pass)
      }
    },
    confirmPasswordRule () {
      return (value) => {
        return value === this.newPassword ? true : this.$t('passwords not match')
      }
    },
    formattedTime () {
      const minutes = Math.floor(this.timer / 60)
      const seconds = this.timer % 60
      return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`
    },
    otpButtonText () {
      return this.timer > 0 ? this.formattedTime : this.$t('Resend')
    },
    headerTitle () {
      if (this.showSingUp) return this.$t('register')
      if (this.showResetPassword || this.OTPResetPassword) { return this.$t('reset password') }
      if (this.OTPVerifyStep) return this.$t('verify phone')
      return this.$t('Login')
    },
    headerSubtitle () {
      if (this.OTPResetPassword) return this.$t('if account exists send code')
      if (this.OTPVerifyStep || this.showResetPassword) {
        return this.$t('verification code sent').replace(
          '<strong></strong>',
          `<strong>${this.phone}</strong>`
        )
      }
      return this.$t('')
    },
    buttonText () {
      if (this.showSingUp) return this.$t('log in')
      return this.$t('please register')
    }
  },
  methods: {
    ruleClass (isValid) {
      if (isValid) return 'rule--valid'
      if (this.newPassword.length > 0) return 'rule--invalid'
      return 'rule--neutral'
    },
    iconColor (isValid) {
      if (isValid) return 'green'
      if (this.newPassword.length > 0) return 'red'
      return 'grey'
    },
    iconName (isValid) {
      if (isValid) return 'mdi-check'
      if (this.newPassword.length > 0) return 'mdi-close'
      return 'mdi-circle'
    },
    login () {
      const valid = this.mobileFormatRule(this.phone)
      if (valid !== true) return
      var app = this
      app.loading = true
      this.$axios
        .post(this.$apipoint + 'v2/login', {
          username: this.phone,
          password: this.password
        })
        .then((response) => {
          // localStorage.setItem('user', response.data.meta)
          localStorage.setItem('hasaccount', response.data.meta.has_account)
          localStorage.setItem('usermobile', response.data.username)
          localStorage.setItem('primaryToken', JSON.stringify(response.data.meta.token))
          app.$axios.defaults.headers.common.x_api_key = response.data.meta.token
          const lastPath = localStorage.getItem('last_path')
          if (lastPath && lastPath !== 'null' && location.host !== 'app.abriron.com') {
            localStorage.setItem('last_path', null)
            app.$router.push({ path: lastPath })
          } else {
            if (app.$route.name === 'mlogin' || app.$route.name === 'mlogout') {
              // localStorage.setItem("apn", "active");
              // app.$router.push({ name: "mDashboard" });
            } else {
              localStorage.setItem('apn', null)
              if (response.data.meta.has_account == true) {
                // set (name + username + avatar) in vuex for showing user data
                app.$store.commit('SET_NAME', response.data.name)
                app.$store.commit('SET_AVATAR', response.data.avatar ? response.data.avatar : null)
                app.$store.commit('SET_USERNAME', response.data.username)
                this.getAccount(response)
              } else {
                app.$router.push('/business-Info')
              }
            }
          }
        })
        .catch((err) => {
          app.serverErrorHandeler(err)
          app.loading = false
        })
        .finally(() => {
          app.overlay = false
        })
    },
    stepForgetPassword () {
      this.OTPResetPassword = true
      this.showOtpForm = false
    },
    getOTPCodePassword () {
      var app = this
      app.loading = true
      const valid = this.mobileFormatRule(this.phone)
      if (valid !== true) {
        app.loading = false
        return
      }
      this.$axios
        .post(this.$apipoint + 'v2/otp/send', {
          mobile: this.phone,
          type: 'reset_password'
        })
        .then((response) => {
          this.OTPResetPassword = false
          this.showResetPassword = true
          this.startTimer()
        })
        .catch((err) => {
          app.loading = false
          app.serverErrorHandeler(err)
        })
        .finally(() => {
          app.loading = false
          app.overlay = false
        })
    },
    // confirmChangePassword() {
    //   var app = this;
    //   app.loading = true;
    //   this.$axios
    //     .post(this.$apipoint + "v2/otp/validate", {
    //       mobile: this.phone,
    //       code: this.OTPcode,
    //       type: 'validateOtp',
    //     })
    //     .then((response) => {
    //       localStorage.setItem("primaryToken", JSON.stringify(response.data.meta.token));
    //       app.$axios.defaults.headers.common.x_api_key = response.data.meta.token;
    //       this.resetPassword(response.data.meta.token);
    //     })
    //     .catch((err) => {
    //       app.loading = false;
    //       app.serverErrorHandeler(err);
    //     })
    //     .finally(() => {
    //       app.overlay = false;
    //     });
    // },
    resetPassword () {
      var app = this
      app.loading = true
      const valid =
        app.passwordChecks.length &&
        app.passwordChecks.upperLower &&
        app.passwordChecks.number &&
        app.passwordChecks.symbol
      if (!valid) return
      app.$axios
        .post(app.$apipoint + 'v2/reset-password',
          {
            mobile: app.phone,
            password: app.newPassword,
            code: app.OTPcode
          }
        )
        .then((response) => {
          app.showResetPassword = false
        })
        .catch((err) => {
          app.loading = false
          app.serverErrorHandeler(err)
        })
        .finally(() => {
          app.loading = false
          app.overlay = false
        })
    },
    returnToLogin () {
      this.showOtpForm = false
    },
    openOTP () {
      this.showOtpForm = true
      this.showResetPassword = false
    },
    goSignup () {
      this.showOtpForm = false
      this.showResetPassword = false
    },
    startTimer () {
      this.timer = 120
      const countdown = setInterval(() => {
        if (this.timer > 0) {
          this.timer--
        } else {
          clearInterval(countdown)
        }
      }, 1000)
    },
    getOTPCode () {
      var app = this
      app.loading = true
      const valid = this.mobileFormatRule(this.phone)
      if (valid !== true) {
        app.loading = false
        return
      }
      this.$axios
        .post(this.$apipoint + 'v2/otp/send', {
          mobile: this.phone
        })
        .then((response) => {
          this.OTPVerifyStep = true
          this.showOtpForm = false
          this.showResetPassword = false
          this.startTimer()
        })
        .catch((err) => {
          app.loading = false
          app.serverErrorHandeler(err)
        })
        .finally(() => {
          app.loading = false
          app.overlay = false
        })
    },
    confirmOTP () {
      var app = this
      app.loading = true
      this.$axios
        .post(this.$apipoint + 'v2/otp/validate', {
          mobile: this.phone,
          code: this.OTPcode,
          type: 'loginByOtp'
        })
        .then((response) => {
          // localStorage.setItem('user', response.data.meta)
          localStorage.setItem('hasaccount', response.data.meta.has_account)
          localStorage.setItem('usermobile', response.data.username)
          localStorage.setItem('primaryToken', JSON.stringify(response.data.meta.token))
          app.$axios.defaults.headers.common.x_api_key = response.data.meta.token
          const lastPath = localStorage.getItem('last_path')
          if (lastPath && lastPath !== 'null' && location.host !== 'app.abriron.com') {
            localStorage.setItem('last_path', null)
            app.$router.push({ path: lastPath })
          } else {
            if (app.$route.name === 'mlogin' || app.$route.name === 'mlogout') {
              // localStorage.setItem("apn", "active");
              // app.$router.push({ name: "mDashboard" });
            } else {
              localStorage.setItem('apn', null)
              if (response.data.meta.has_account == true) {
                // set (name + username + avatar) in vuex for showing user data
                app.$store.commit('SET_NAME', response.data.name)
                app.$store.commit('SET_AVATAR', response.data.avatar ? response.data.avatar : null)
                app.$store.commit('SET_USERNAME', response.data.username)
                this.getAccount(response)
              } else if (response.data.meta.has_account == false) {
                app.$router.push('/business-Info')
              } else {
                app.$router.push('/login')
              }
            }
          }
        })
        .catch((err) => {
          app.loading = false
          this.serverErrorHandeler(err)
        })
        .finally(() => {
          // app.loading = false;
          app.overlay = false
        })
    },
    EditPhone () {
      this.OTPVerifyStep = false
      this.showOtpForm = true
    },
    EditPhonePass () {
      this.showResetPassword = false
      this.OTPResetPassword = true
    },
    routeToRegister () {
      this.$emit('update:signUpSection', true)
      this.$emit('switchMode', true)
    },
    resendOTP () {
      if (this.timer === 0) {
        if (this.OTPVerifyStep) {
          this.getOTPCode()
        } else {
          this.getOTPCodePassword()
        }
        this.startTimer()
      }
    },
    getAccount (value) {
      var app = this
      app.loading = true
      app.$axios
        .post(app.$apipoint + 'v2/get-user-accounts', {
          headers: {
            x_api_key: value.data.meta.token
          }
        })
        .then((response) => {
          app.$emit('business-selector', {
            hasAccount: value.data.meta.has_account,
            Authorization: value.data.meta.token,
            userData: response.data.accounts
          })
        })
        .catch((err) => {
          app.loading = false
          app.serverErrorHandeler(err)
        })
        .finally(() => {
          app.overlay = false
          app.loading = false
        })
    }
  }
}
</script>

<style scoped src="./auth-form.css"></style>
